import DeliverableDialog from './DeliverableDialog';
import { useCreateProject } from './CreateProjectContext';
import type { DeliverableState } from './DeliverableDialog';
import type { Deliverable } from './CreateProjectContext';
import DeliverableCard from './DeliverableCard';
import ProjectSummaryCard from './ProjectSummaryCard';
import { Button } from '@/components/ui/button';
import { Link, useNavigate } from 'react-router';
import { DragDropProvider } from '@dnd-kit/react';
import type { DragOverEvent, DragEndEvent } from '@dnd-kit/react';
import { useRef } from 'react';
import { TooltipProvider } from '@/components/ui/tooltip';

export default function Deliverables() {
  const { formState, setFormState, resetForm } = useCreateProject();
  const navigate = useNavigate();

  const dragIntent = useRef<{ sourceId: string; targetId: string } | null>(
    null
  );

  const handleDragOver: DragOverEvent = (event) => {
    const { source, target } = event.operation;
    if (!source || !target || source.id === target.id) return;

    dragIntent.current = {
      sourceId: source.id as string,
      targetId: target.id as string,
    };
  };

  const handleDragEnd: DragEndEvent = () => {
    if (!dragIntent.current) return;
    const { sourceId, targetId } = dragIntent.current;
    dragIntent.current = null;

    setFormState((prev) => {
      const items = [...prev.deliverables];
      const fromIndex = items.findIndex((i) => i.id === sourceId);
      const toIndex = items.findIndex((i) => i.id === targetId);
      if (fromIndex === -1 || toIndex === -1) return prev;

      const [moved] = items.splice(fromIndex, 1);
      items.splice(toIndex, 0, moved);

      return {
        ...prev,
        deliverables: items,
      };
    });
  };

  function onAdd(deliverable: DeliverableState) {
    const exsistingCount = formState.deliverables.filter(
      (d) => d.videoType === deliverable.videoType
    ).length;
    const newDeliverables = Array.from(
      { length: deliverable.quantity },
      (_, i) => {
        const totalOfType = exsistingCount + i + 1;
        const needsNumber = exsistingCount > 0 || deliverable.quantity > 1;

        return {
          ...deliverable,
          id: crypto.randomUUID(),
          deliverableName: `${deliverable.videoType}${needsNumber ? ` ${totalOfType}` : ''}`,
          notes: '',
          resolution: 'Match Source',
          frameRate: 'Match Source',
          deliveryFormat: 'MP4-H264',
          maxFileSize: 'N/A',
        } as Deliverable;
      }
    );

    setFormState((prev) => ({
      ...prev,
      deliverables: [...prev.deliverables, ...newDeliverables],
    }));
  }

  function onUpdate(index: number, updated: Partial<Deliverable>) {
    setFormState((prev) => {
      const updatedDeliverables = [...prev.deliverables];
      updatedDeliverables[index] = {
        ...updatedDeliverables[index],
        ...updated,
      };
      return {
        ...prev,
        deliverables: updatedDeliverables,
      };
    });
  }

  function handleCancel() {
    resetForm();
    navigate('/client');
  }

  function onDelete(index: number) {
    setFormState((prev) => {
      const updatedDeliverables = prev.deliverables.filter(
        (_, i) => i !== index
      );
      return {
        ...prev,
        deliverables: updatedDeliverables,
      };
    });
  }

  function onDuplicate(index: number) {
    setFormState((prev) => {
      const updatedDeliverables = [...prev.deliverables];
      updatedDeliverables.splice(index + 1, 0, {
        ...prev.deliverables[index],
        id: crypto.randomUUID(),
      });
      return {
        ...prev,
        deliverables: updatedDeliverables,
      };
    });
  }

  const allDeliverableCards = formState.deliverables.map(
    (deliverable, index) => (
      <DeliverableCard
        key={deliverable.id}
        index={index}
        deliverable={deliverable}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onDuplicate={onDuplicate}
        className="my-5"
      />
    )
  );

  return (
    <div className="grid gap-12 min-h-full">
      <div className="col-span-2 flex flex-col max-w-300">
        <h1 className="text-2xl font-bold mb-6">Project Deliverables</h1>
        <ProjectSummaryCard />
        <div className="py-1 flex flex-row gap-8 items-center">
          <DeliverableDialog onAdd={onAdd} />
          {formState.deliverables.length === 0 && (
            <p className="text-muted-foreground">
              Add the videos you would like us to edit.
            </p>
          )}
        </div>
        <TooltipProvider delayDuration={800}>
          <DragDropProvider
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
          >
            <div className="flex-1">{allDeliverableCards}</div>
          </DragDropProvider>
        </TooltipProvider>
        <div className="py-3 flex justify-between gap-2 w-full">
          <Button
            variant={'ghost'}
            className="cursor-pointer"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <div>
            <Button variant={'outline'} className="cursor-pointer mr-2" asChild>
              <Link to="../">Back</Link>
            </Button>
            <Button
              className="cursor-pointer"
              disabled={formState.deliverables.length === 0}
              onClick={() => navigate('../review')}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
